// src/pages/Feed.js
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../contexts/PageContext";
import { useCurrentUser } from "../hooks/useAuth";
import { 
  useWallFeeds, 
  useUserProfile, 
  useFollowStats,
  useCreatePost,
  useLikePost,
  useCommentOnPost,
  useDeletePost,
  useSearchUsers
} from "../hooks/useFeed";
import CreatePostForm from "../components/common/CreatePostForm";
import UserSearchBar from "../components/common/UserSearchBar";
import UserSidebar from "../components/feed/UserSidebar";
import FeedPostsList from "../components/feed/FeedPostsList";

const Feed = () => {
  const { t } = useTranslation();
  const { updatePageTitle } = useMainPageTitle();
  const [newPost, setNewPost] = useState({ content: "", image: null });
  const [comment, setComment] = useState("");
  const [activeCommentPost, setActiveCommentPost] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // React Query hooks
  const { data: currentUser } = useCurrentUser();
  const { 
    data: feedData, 
    isLoading: feedLoading, 
    error: feedError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useWallFeeds();
  
  const { data: userProfile } = useUserProfile(currentUser?.userName);
  
  // Use either _id or id, whichever is available
  const userId = currentUser?._id || currentUser?.id;
  
  const { data: followStats, error: followStatsError } = useFollowStats(userId);
  
  // Debug follow stats only if there's data or error
  if (followStats || followStatsError) {
    console.log('[Feed] Follow stats result:', { followStats, followStatsError });
  }

  
  const { data: searchResults, isLoading: searchLoading } = useSearchUsers(searchQuery);

  // Mutations
  const createPostMutation = useCreatePost();
  const likePostMutation = useLikePost();
  const commentMutation = useCommentOnPost();
  const deletePostMutation = useDeletePost();

  useEffect(() => {
    updatePageTitle(t("feed.title"), t("feed.startSharingContent"));
  }, [updatePageTitle, t]);

  // Flatten infinite query data
  const posts = feedData?.pages?.flatMap(page => page.data) || [];

  // Event handlers
  const handlePostSubmit = async (e) => {
    e.preventDefault();
    console.log("Create post submitted");
    console.log("Current user:", currentUser);
    console.log("New post content:", newPost.content);
    
    if (!(currentUser?._id || currentUser?.id) || !newPost.content.trim()) {
      console.log("Validation failed - User ID:", currentUser?._id || currentUser?.id, "Content:", newPost.content.trim());
      return;
    }

    try {
      const formData = new FormData();
      formData.append("content", newPost.content);
      formData.append("referenceUser", currentUser._id || currentUser.id);
      if (newPost.image) {
        formData.append("image", newPost.image);
      }

      console.log("Calling createPostMutation...");
      await createPostMutation.mutateAsync(formData);
      console.log("Post created successfully");
      setNewPost({ content: "", image: null });
    } catch (error) {
      console.error("Error creating post:", error);
    }
  };

  const handleLike = async (postId) => {
    try {
      await likePostMutation.mutateAsync(postId);
    } catch (error) {
      console.error("Error liking/unliking post:", error);
    }
  };

  const handleCommentSubmit = async (postId) => {
    if (!comment.trim()) return;

    try {
      await commentMutation.mutateAsync({ 
        postId, 
        content: comment, 
        currentUser 
      });
      setComment("");
      // Don't close the comment section after posting
    } catch (error) {
      console.error("Error posting comment:", error);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm(t("feed.deletePost") + "?")) return;

    try {
      await deletePostMutation.mutateAsync(postId);
    } catch (error) {
      console.error("Error deleting post:", error);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  if (feedLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#E05720] border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">{t("pages.loading.pleaseWait")}</p>
        </div>
      </div>
    );
  }

  if (feedError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">{t("errors.failedToLoad")}</h2>
            <p className="text-gray-600">{t("feed.refreshPageError")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="">

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Enhanced Left Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-transform duration-200 hover:shadow-xl">
                <div className="bg-gradient-to-r from-[#E05720]/10 to-[#E05720]/5 p-4 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <svg className="w-5 h-5 text-[#E05720] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {t("feed.profile")}
                  </h2>
                </div>
                <div className="p-4">
                  <UserSidebar
                    userProfile={userProfile}
                    followStats={followStats}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Enhanced Search Bar */}
            <div className="bg-white rounded-2xl shadow-lg p-6 transition-all duration-200 hover:shadow-xl">
              <div className="flex items-center mb-4">
                <svg className="w-5 h-5 text-[#E05720] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900">{t("feed.discoverPeople")}</h3>
              </div>
              <UserSearchBar 
                onSearch={handleSearch}
                searchResults={searchResults}
                isLoading={searchLoading}
              />
            </div>

            {/* Enhanced Create Post Form */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-200 hover:shadow-xl">
              <div className="bg-gradient-to-r from-[#E05720]/10 to-[#E05720]/5 p-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <svg className="w-5 h-5 text-[#E05720] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t("feed.createNewPost")}
                </h3>
              </div>
              <div className="p-6">
                <CreatePostForm
                  newPost={newPost}
                  setNewPost={setNewPost}
                  onSubmit={handlePostSubmit}
                  isLoading={createPostMutation.isPending}
                  currentUser={currentUser}
                />
              </div>
            </div>

            {/* Enhanced Posts Feed */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-[#E05720]/10 to-[#E05720]/5 p-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <svg className="w-5 h-5 text-[#E05720] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                    </svg>
                    {t("feed.latestPosts")}
                  </h3>
                  {posts.length > 0 && (
                    <div className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      {posts.length} {posts.length === 1 ? t("feed.post") : t("feed.posts")}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="p-6 min-h-[1200px]">
                {posts.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{t("feed.noPostsYet")}</h3>
                    <p className="text-gray-600 max-w-sm mx-auto">
                      {t("feed.beFirstToShare")}
                    </p>
                  </div>
                ) : (
                  <FeedPostsList
                    posts={posts}
                    currentUser={currentUser}
                    onLike={handleLike}
                    onComment={handleCommentSubmit}
                    onDelete={handleDeletePost}
                    comment={comment}
                    setComment={setComment}
                    activeCommentPost={activeCommentPost}
                    setActiveCommentPost={setActiveCommentPost}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    loadMore={loadMore}
                    commentMutation={commentMutation}
                    likePostMutation={likePostMutation}
                    deletePostMutation={deletePostMutation}
                    isLiking={likePostMutation.isPending}
                    isCommenting={commentMutation.isPending}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feed;
